/**
 * P3-4 — Marcadores de cuenta externa que Azure AD antepone al `displayName`.
 *
 * En QA el directorio devuelve nombres como `(Ext.) De la Cruz Morales, Ernesto`. El prefijo es
 * una convención administrativa del tenant, no parte del nombre de la persona, y el prototipo
 * nunca lo muestra.
 */
const EXTERNAL_MARKER = /^\s*[([]\s*(?:ext\.?|extern[oa]?|external)\s*[)\]]\s*[-–—:]?\s*/i;

/**
 * Nombre natural a partir del `displayName` crudo de Azure AD.
 *
 * REGLA ADOPTADA (P3-4), en este orden:
 *   1. Se retira el marcador de cuenta externa: `(Ext.)`, `(Ext)`, `[Externo]`, `(External)`…
 *   2. Si lo que queda tiene la forma `Apellidos, Nombre` —una sola coma, con texto a ambos
 *      lados— se invierte a `Nombre Apellidos`. Con dos o más comas NO se toca: no hay forma
 *      fiable de saber dónde acaba el apellido, y adivinarlo sería peor que no hacer nada.
 *   3. Se colapsan los espacios repetidos.
 *
 * Cualquier otro caso se devuelve tal cual: la función nunca descarta información, solo reordena.
 *
 * DÓNDE SE APLICA. En los dos puntos por los que entra un nombre a la interfaz de producto:
 * `useCurrentUser` (la identidad propia → menú, perfil, firma de los post-its) y
 * `toPersonSummary` (las demás personas → buscador de compañeros, muro, Wrapped).
 *
 * DÓNDE NO. En la administración de usuarios (`UsersAdminView`) se sigue mostrando el valor
 * CRUDO del directorio: ahí el `displayName` es el identificador con el que se da de alta y se
 * busca contra el backend, y normalizarlo haría que lo que se ve en pantalla no coincida con lo
 * que hay que teclear para encontrarlo.
 */
export function toNaturalName(displayName: string | null | undefined): string {
  const cleaned = (displayName ?? '').replace(EXTERNAL_MARKER, '').trim();
  if (!cleaned) return '';

  const parts = cleaned.split(',');
  if (parts.length === 2) {
    const surnames = parts[0].trim();
    const given = parts[1].trim();
    if (surnames && given) return `${given} ${surnames}`.replace(/\s+/g, ' ');
  }
  return cleaned.replace(/\s+/g, ' ');
}

/**
 * Iniciales (hasta 2 letras) de un nombre o correo.
 *
 * Las dos PRIMERAS palabras. Con el formato que devuelve el directorio de Sigma —«Apellidos,
 * Nombre», que `toNaturalName` invierte a «Nombre Apellido1 Apellido2»— eso da nombre de pila +
 * apellido PATERNO, que es el que identifica a una persona en español. Tomar la última palabra
 * daría el materno: «Miguel Torres Vega» saldría como MV en vez de MT.
 *
 * Tolera nulos y cadenas vacías porque se monta sobre datos del directorio, donde `displayName`
 * puede faltar. Devuelve `?` en vez de una cadena vacía: un círculo en blanco se lee como un fallo
 * de carga, no como un nombre ausente.
 *
 * Implementación única (2026-09-03): reemplaza nueve copias sueltas (`getInitials` de
 * `analytics.ts`, `initialsFor` de `brillamosShared.tsx`, `initialsFromName` de `useUsers.ts`, y
 * las funciones locales de `UserConfigFAB`, `Sidebar`, `ActiveSurveyInline`, `MatrixBody`,
 * `SurveyCard` y `SurveyQuiz`), que aplicaban reglas ligeramente distintas y hacían que la misma
 * persona saliera con iniciales diferentes según la pantalla.
 */
export function getInitials(name: string | null | undefined): string {
  const limpio = (name ?? '').trim().replace(/\s+/g, ' ');
  if (!limpio) return '?';

  const palabras = limpio.split(' ');
  if (palabras.length === 1) return palabras[0].slice(0, 2).toUpperCase();
  return (palabras[0][0] + palabras[1][0]).toUpperCase();
}
